import React, { FC, useEffect, useRef, useState } from 'react';
import { Button, Card, Form, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import Moment from 'react-moment';
import { Link } from 'react-router-dom';
import useSubmissionList from '../../../hooks/api/submission/useSubmissionList';
import _ from 'lodash';
import { getSecuritySection, SecuritySections, SecurityModel } from '../../../models/security/security';
import { useIntl } from 'react-intl';
import { formatToString } from '../../../components/form/public/payment/fieldToString';
import { FieldType } from '../../../models/form/fieldType';
import EmptyList from '../../../components/layout/emptyList/EmptyList';
import LoadingSpinner from '../../../components/layout/loadingSpinner/LoadingSpinner';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useHistory, useParams } from 'react-router';
import { getText } from '../../../utilities/CultureUtilities';
import { FallbackPage } from '../../../components/layout/navbar/Navbar';
import ModalComponent from '../../../components/modal/ModalComponent';
import { Submissions } from '../../../models/submission/submission';
import { Formik } from 'formik';
import * as Yup from 'yup';
import useResendSubmissionEmail from '../../../hooks/api/submission/useResendSubmissionEmail';
import SearchInput from '../../../components/library/inputs/search/SearchInput';
import useDebounce from '../../../hooks/useDebounce';
import PaginationComponent from '../../../components/pagination/PaginationComponent';
import useDimension from '../../../hooks/useDimension';

const excludedFieldType: FieldType[] = [
  FieldType.Toggle,
  FieldType.CheckBox,
  FieldType.Video,
  FieldType.EmbeddedPhoto,
  FieldType.TakePicture,
  FieldType.DocumentViewer,
  FieldType.UploadFile,
  FieldType.WebLink,
  FieldType.Payment,
  FieldType.Acknowledgment,
  FieldType.Captcha,
  FieldType.Signature,
  FieldType.HTML,
  FieldType.Select,
  FieldType.PaymentOptionList,
  FieldType.PaymentChoiceList,
  FieldType.OptionList,
  FieldType.Products,
  FieldType.StaticText,
];

const nbPerPage = 12;

const actionsWidth = 120;

interface MatchParams {
  formId: string;
}

type header = { id: string; name: string };

const SubmissionListPage: FC = () => {
  const { formId } = useParams<MatchParams>();
  const {
    currentSubmissionList,
    totalCount,
    setCurrentFormId,
    isLoading,
    currentFormSteps,
    currentFormName,
    reloadSubmissions,
  } = useSubmissionList(formId, nbPerPage);
  const [nbOfPages, setNbOfPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [headers, setHeaders] = useState<header[]>([]);
  const [formSubmissionSecurity, setFormSubmissionSecurity] = useState<SecurityModel | null>(null);
  const [formName, setFormName] = useState<string>('');
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
  const [query, setQuery] = useState<string>('');
  const [selectedSubmission, setSelectedSubmission] = useState<Submissions | null>(null);
  const security = useSelector((state: RootState) => state.organization.organization?.security);
  const intl = useIntl();
  const history = useHistory();
  const organizationId = useSelector((state: RootState) => state.organization.organization?.id) as string;
  const {
    resendSubmissionEmail,
    setOrganizationId,
    isLoading: isLoadingResendEmail,
  } = useResendSubmissionEmail(organizationId);
  const [sentSubmissionEmail, setSentSubmissionEmail] = useState<string[]>([]);
  const tableRef = useRef<HTMLDivElement>(null);
  const { width } = useDimension(tableRef);

  useEffect(() => {
    if (formId) {
      setCurrentFormId(formId);
    }
  }, [currentSubmissionList]);

  useEffect(() => {
    if (organizationId) {
      setOrganizationId(organizationId);
    }
  }, [organizationId]);

  useEffect(() => {
    setNbOfPages(Math.ceil(totalCount / nbPerPage));
  }, [totalCount]);

  useEffect(() => {
    if (currentFormSteps && currentFormSteps.length > 0) {
      const headerCount = Math.floor((width - actionsWidth) / 200) - 1;
      createColumnHeaders(headerCount);
    }
  }, [currentFormSteps, width]);

  useEffect(() => {
    if (currentFormName) {
      setFormName(currentFormName);
    }
  }, [currentFormName]);

  useEffect(() => {
    if (security) {
      setFormSubmissionSecurity(getSecuritySection(security, SecuritySections.FORM_SUBMISSION));
    }
    if (formSubmissionSecurity && !formSubmissionSecurity?.read) {
      history.push(FallbackPage);
    }
  }, [security, formSubmissionSecurity]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    reloadSubmissions(newPage, query);
  };

  const createColumnHeaders = (headerCount: number) => {
    const headersList: header[] = [];
    const formStep = currentFormSteps[0];
    for (const section of formStep.sections) {
      for (const field of section.fields) {
        if (!excludedFieldType.includes(field.type)) {
          headersList.push({ id: field.id, name: getText(field.text, intl.locale) });
          if (headersList.length >= headerCount) {
            setHeaders(headersList);
            headersList.push({ id: 'date', name: 'Date' });
            return;
          }
        }
      }
    }
    headersList.push({ id: 'date', name: 'Date' });
    setHeaders(headersList);
  };

  const renderTooltip = (text) => <Tooltip id="button-tooltip">{text}</Tooltip>;

  const EmailSchema = Yup.object().shape({
    email: Yup.string()
      .email(intl.formatMessage({ id: 'fields.fieldErrors.email' }))
      .required(),
  });

  const resetModal = () => {
    setShowEmailModal(false);
    setSelectedSubmission(null);
  };

  const renderResendEmailModal = () => {
    return (
      <Formik
        enableReinitialize={true}
        initialValues={{
          email: selectedSubmission?.customerEmail || '',
        }}
        validationSchema={EmailSchema}
        onSubmit={async (values, { resetForm }) => {
          if (selectedSubmission) {
            const result = await resendSubmissionEmail(formId, selectedSubmission.id, values.email);
            if (result > 0) {
              setSentSubmissionEmail([...sentSubmissionEmail, selectedSubmission.id]);
            }

            resetModal();
            resetForm();
          }
        }}
      >
        {({ errors, touched, values, handleChange, handleBlur, handleSubmit, resetForm }) => (
          <ModalComponent
            closeSettings={() => {
              resetModal();
              resetForm();
            }}
            buttons={[
              {
                text: intl.formatMessage({ id: 'formList.resendEmailModal.send' }),
                buttonVariant: 'primary',
                loading: isLoadingResendEmail,
                onClick: async () => {
                  handleSubmit();
                },
              },
              {
                text: intl.formatMessage({ id: 'cancel' }),
                buttonVariant: 'secondary',
                onClick: () => {
                  resetModal();
                  resetForm();
                },
              },
            ]}
            show={showEmailModal}
            modalHeading={intl.formatMessage({
              id: 'formList.resendEmailModal.sendSubmissionEmail',
            })}
          >
            <div className="d-flex justify-content-center flex-column px-5">
              <label className="modal-label">
                <Form.Label>{intl.formatMessage({ id: 'formList.resendEmailModal.email' })}</Form.Label>
                <Form.Control
                  disabled={isLoading}
                  type="email"
                  name="email"
                  placeholder={intl.formatMessage({ id: 'formList.resendEmailModal.email' })}
                  isInvalid={touched.email && !!errors.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  style={{ background: 'white' }}
                />
                <Form.Control.Feedback className="text-center" type="invalid">
                  *{errors.email && <span>{errors.email}</span>}
                </Form.Control.Feedback>
              </label>
            </div>
          </ModalComponent>
        )}
      </Formik>
    );
  };

  const onSearchChange = useDebounce(() => {
    setCurrentPage(1);
    reloadSubmissions(1, query);
  });

  return (
    <div>
      {renderResendEmailModal()}
      <Card>
        <Card.Header className="d-inline-flex align-items-center justify-content-start">
          <span>{intl.formatMessage({ id: 'formList.submission.formName' })}</span>
          <span className="form-name">{getText(formName, intl.locale)}</span>
        </Card.Header>
        <Card.Body>
          <SearchInput
            value={query}
            className={'flex-fill mb-4'}
            onChange={(e) => {
              setQuery(e.currentTarget.value);
              onSearchChange();
            }}
          ></SearchInput>
          <div className="position-relative" ref={tableRef}>
            {isLoading && <LoadingSpinner />}
            {currentSubmissionList && currentSubmissionList.length > 0 && (
              <div>
                <div>
                  <div>
                    <div className="d-flex submissions-row">
                      {headers &&
                        headers.map((header) => {
                          return (
                            <OverlayTrigger
                              key={header.id}
                              delay={{ show: 50, hide: 100 }}
                              overlay={renderTooltip(header.name)}
                              placement="top-start"
                            >
                              <div className="submissions-list-header">
                                <span>{header.name}</span>
                              </div>
                            </OverlayTrigger>
                          );
                        })}
                      <div key="actions" className="text-end submissions-list-header" style={{ width: actionsWidth }}>
                        Actions
                      </div>
                    </div>
                  </div>
                  <div className="submissions-body">
                    {currentSubmissionList.map((row) => {
                      const hasBeenSent = _.find(sentSubmissionEmail, (id) => id === row.id);
                      return (
                        <div className="d-flex submissions-row" key={row.id}>
                          {headers.map((key) => {
                            if (key.id === 'date') {
                              return (
                                <div className="submissions-list-value submissions-cell" key={`${row.id}-${key.id}`}>
                                  <Moment format="LLL">{row[key.id]}</Moment>
                                </div>
                              );
                            }
                            let fieldValue = '';
                            const field = _.find(row.fields, { id: key.id });
                            if (field) {
                              fieldValue = formatToString(field, JSON.parse(field.value).value) || '';
                            }
                            return (
                              <div className="submissions-list-value submissions-cell" key={`${row.id}-${key.id}`}>
                                <span>{fieldValue}</span>
                              </div>
                            );
                          })}
                          <div className="text-end submissions submissions-list-value" style={{ width: actionsWidth }}>
                            <OverlayTrigger
                              delay={{ show: 50, hide: 100 }}
                              overlay={renderTooltip(intl.formatMessage({ id: 'formList.submission.view' }))}
                              placement="top"
                            >
                              <Button
                                variant="primary"
                                className="d-inline-flex align-items-center justify-content-center me-1"
                                onClick={() => history.push(`/registrations/submissions/${formId}/${row.id}`)}
                                disabled={!formSubmissionSecurity?.read}
                              >
                                <div className="d-inline-flex">
                                  <span className="mdi mdi mdi-eye-settings-outline"></span>
                                </div>
                              </Button>
                            </OverlayTrigger>
                            <OverlayTrigger
                              delay={{ show: 50, hide: 100 }}
                              overlay={renderTooltip(
                                intl.formatMessage({ id: 'formList.resendEmailModal.sendSubmissionEmail' }),
                              )}
                              placement="top"
                            >
                              <span>
                                {hasBeenSent && (
                                  <Button
                                    variant="success"
                                    className="d-inline-flex align-items-center justify-content-center me-1"
                                  >
                                    <div className="d-inline-flex">
                                      <span className="mdi mdi mdi-check"></span>
                                    </div>
                                  </Button>
                                )}
                                {!hasBeenSent && (
                                  <Button
                                    variant="primary"
                                    className="d-inline-flex align-items-center justify-content-center me-1"
                                    onClick={() => {
                                      setSelectedSubmission(row);
                                      setShowEmailModal(true);
                                    }}
                                    disabled={!formSubmissionSecurity?.read}
                                  >
                                    {selectedSubmission?.id === row.id && isLoadingResendEmail ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <div className="d-inline-flex">
                                        <span className="mdi mdi mdi-email-sync"></span>
                                      </div>
                                    )}
                                  </Button>
                                )}
                              </span>
                            </OverlayTrigger>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            {currentSubmissionList && currentSubmissionList.length > 0 && nbOfPages && nbOfPages > 1 && (
              <PaginationComponent
                currentPage={currentPage}
                pageSize={nbPerPage}
                totalCount={totalCount}
                onChange={handlePageChange}
              ></PaginationComponent>
            )}
            {currentSubmissionList && currentSubmissionList.length == 0 && (
              <div className="d-flex flex-column justify-content-center align-items-center">
                <EmptyList message={intl.formatMessage({ id: 'formList.submission.noSubmissions' })} />
                <Link to={`/registrations`}>
                  <Button variant="primary" className="me-1">
                    <span>{intl.formatMessage({ id: 'formList.buttons.returnToForms' })}</span>
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default SubmissionListPage;
